import { Clock, Loader, CheckCircle, XCircle, Package, Truck, Edit, ShoppingBag, Warehouse, Ban, Send, AlertCircle, RotateCcw, PackageCheck, Eye, FileCheck, ArrowRight } from 'lucide-react';

/**
 * Global Order Status Configuration
 * 
 * Maps backend statusCode values to frontend display properties.
 * Statuses are data-driven (stored in DB per-tenant workflow),
 * so this config covers all known/expected status codes with a
 * smart fallback for unknown ones.
 * 
 * Each status has:
 *   - label:     Human-readable display name
 *   - color:     Tailwind badge classes (bg + text + border)
 *   - accent:    Solid Tailwind color class for left-bar accents on cards
 *   - icon:      Lucide icon component
 */
export const statusConfig = {
    // ── Order Lifecycle ──
    'CREATED': {
        label: 'New Order',
        color: 'bg-blue-50 text-blue-700 border border-blue-200',
        accent: 'bg-blue-500',
        icon: ShoppingBag
    },
    'NEW_ORDER': {
        label: 'New Order',
        color: 'bg-blue-50 text-blue-700 border border-blue-200',
        accent: 'bg-blue-500',
        icon: ShoppingBag
    },
    'CONFIRMED': {
        label: 'Confirmed',
        color: 'bg-sky-50 text-sky-700 border border-sky-200',
        accent: 'bg-sky-500',
        icon: FileCheck
    },
    'ACKNOWLEDGED': {
        label: 'Acknowledged',
        color: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
        accent: 'bg-indigo-500',
        icon: CheckCircle
    },
    'PROCESSING': {
        label: 'Processing',
        color: 'bg-blue-50 text-blue-700 border border-blue-200',
        accent: 'bg-blue-500',
        icon: Loader
    },
    'PENDING': {
        label: 'Pending',
        color: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
        accent: 'bg-yellow-500',
        icon: Clock
    },

    // ── Warehouse Flow ──
    'SENT_TO_WAREHOUSE': {
        label: 'Sent to Warehouse',
        color: 'bg-purple-50 text-purple-700 border border-purple-200',
        accent: 'bg-purple-500',
        icon: Send
    },
    'PENDING_WAREHOUSE': {
        label: 'Pending Warehouse',
        color: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
        accent: 'bg-indigo-500',
        icon: Warehouse
    },
    'IN_WAREHOUSE': {
        label: 'In Warehouse',
        color: 'bg-violet-50 text-violet-700 border border-violet-200',
        accent: 'bg-violet-500',
        icon: Warehouse
    },
    'APPROVED': {
        label: 'Approved',
        color: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
        accent: 'bg-emerald-500',
        icon: CheckCircle
    },
    'REVISED': {
        label: 'Revised',
        color: 'bg-amber-50 text-amber-700 border border-amber-200',
        accent: 'bg-amber-500',
        icon: Edit
    },
    'REJECTED': {
        label: 'Returned',
        color: 'bg-orange-50 text-orange-700 border border-orange-200',
        accent: 'bg-orange-500',
        icon: RotateCcw
    },
    'REQUEST_FOR_MODIFICATION': {
        label: 'Modification Required',
        color: 'bg-orange-50 text-orange-700 border border-orange-200',
        accent: 'bg-orange-500',
        icon: RotateCcw
    },

    // ── Fulfillment ──
    'READY': {
        label: 'Ready',
        color: 'bg-teal-50 text-teal-700 border border-teal-200',
        accent: 'bg-teal-500',
        icon: PackageCheck
    },
    'PACKED': {
        label: 'Packed',
        color: 'bg-teal-50 text-teal-700 border border-teal-200',
        accent: 'bg-teal-500',
        icon: Package
    },
    'DISPATCHED': {
        label: 'Dispatched',
        color: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
        accent: 'bg-indigo-500',
        icon: Truck
    },
    'DESPATCHED': {
        label: 'Dispatched',
        color: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
        accent: 'bg-indigo-500',
        icon: Truck
    },
    'OUT_FOR_DELIVERY': {
        label: 'Out for Delivery',
        color: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
        accent: 'bg-indigo-500',
        icon: Truck
    },
    'DELIVERED': {
        label: 'Delivered',
        color: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
        accent: 'bg-emerald-600',
        icon: CheckCircle
    },

    // ── Terminal ──
    'COMPLETED': {
        label: 'Completed',
        color: 'bg-green-50 text-green-700 border border-green-200',
        accent: 'bg-green-600',
        icon: CheckCircle
    },
    'CANCELLED': {
        label: 'Cancelled',
        color: 'bg-gray-100 text-gray-500 border border-gray-200',
        accent: 'bg-gray-400',
        icon: Ban
    },
    'RETURNED': {
        label: 'Returned',
        color: 'bg-orange-50 text-orange-700 border border-orange-200',
        accent: 'bg-orange-500',
        icon: RotateCcw
    },
    'REFUNDED': {
        label: 'Refunded',
        color: 'bg-rose-50 text-rose-700 border border-rose-200',
        accent: 'bg-rose-500',
        icon: RotateCcw
    },

    // ── Review / Inspection ──
    'UNDER_REVIEW': {
        label: 'Under Review',
        color: 'bg-orange-50 text-orange-700 border border-orange-200',
        accent: 'bg-orange-500',
        icon: Eye
    },
    'ON_HOLD': {
        label: 'On Hold',
        color: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
        accent: 'bg-yellow-500',
        icon: Clock
    }
};

/**
 * Default config for unknown statuses — provides a sensible fallback
 * so that any new status code added to the backend workflow still renders.
 */
const defaultConfig = {
    label: null,       // will be derived from statusCode
    color: 'bg-gray-100 text-gray-600 border border-gray-200',
    accent: 'bg-gray-400',
    icon: AlertCircle
};

export const getStatusConfig = (status) => statusConfig[status] || defaultConfig;

export const getStatusColor = (status) => {
    return (statusConfig[status] || defaultConfig).color;
};

export const getStatusLabel = (status) => {
    return statusConfig[status]?.label || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
};

export const getStatusIcon = (status) => {
    return (statusConfig[status] || defaultConfig).icon;
};

export const getStatusAccent = (status) => {
    return (statusConfig[status] || defaultConfig).accent;
};
